import React, { useState, useEffect, useRef } from "react";
import {
  Copy,
  Download,
  Share2,
  Check,
  LoaderCircle,
  Plus,
  Trash2,
  Printer,
  ArrowRight,
} from "lucide-react";
import {
  publicSchema,
  invoiceTotals,
  savingsResult,
  utmLink,
} from "../shared/tool-functions.mjs";
const save = (value, name, type = "text/plain") => {
  const url = URL.createObjectURL(new Blob([value], { type }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
export function ResultActions({
  text,
  name = "bizaipro-result.txt",
  type = "text/plain",
  share = false,
}) {
  const [message, setMessage] = useState("");
  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setMessage("Copied");
    } catch {
      setMessage("Copy is unavailable in this browser. Use Download instead.");
    }
  }
  async function shareResult() {
    try {
      if (navigator.share)
        await navigator.share({ title: "BizAIPro result", text });
      else {
        await navigator.clipboard.writeText(text);
        setMessage("Share text copied");
      }
    } catch {
      setMessage("Sharing was not completed.");
    }
  }
  return (
    <>
      <div className="result-actions">
        <button type="button" className="button light small" onClick={copy}>
          <Copy size={16} />
          Copy result
        </button>
        <button
          type="button"
          className="button light small"
          onClick={() => save(text, name, type)}
        >
          <Download size={16} />
          Download
        </button>
        {share && (
          <button
            type="button"
            className="button light small"
            onClick={shareResult}
          >
            <Share2 size={16} />
            Share
          </button>
        )}
      </div>
      <span className="action-status" role="status">
        {message}
      </span>
    </>
  );
}
export function NetworkTool({ kind = "website", compact = false }) {
  const [value, setValue] = useState(""),
    [strategy, setStrategy] = useState("mobile"),
    [busy, setBusy] = useState(false),
    [result, setResult] = useState(null),
    [error, setError] = useState("");
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const response = await fetch("/api/public/" + kind, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ value, strategy }),
        signal: AbortSignal.timeout(120000),
      });
      const data = await response.json();
      if (!response.ok)
        throw new Error(data.error || "Analysis is unavailable right now.");
      if (mounted.current) setResult(data);
    } catch (err) {
      if (mounted.current)
        setError(
          err.name === "TimeoutError"
            ? "Analysis timed out. Please try again."
            : err.message,
        );
    } finally {
      if (mounted.current) setBusy(false);
    }
  }
  return (
    <section
      className={compact ? "website-checker compact" : "website-checker"}
      aria-label={
        kind === "website"
          ? "Website health analysis"
          : "Public website or domain lookup"
      }
    >
      <form onSubmit={submit}>
        <label htmlFor={"public-input-" + kind}>
          {["dns", "domain"].includes(kind)
            ? "Domain name"
            : "Public website URL"}
        </label>
        <div className="checker-input">
          <input
            id={"public-input-" + kind}
            type={["dns", "domain"].includes(kind) ? "text" : "url"}
            required
            maxLength={2000}
            placeholder={
              ["dns", "domain"].includes(kind)
                ? "example.com"
                : "https://example.com"
            }
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
          <button className="button" disabled={busy}>
            {busy ? (
              <LoaderCircle size={18} className="spin" />
            ) : (
              <ArrowRight size={18} />
            )}{" "}
            {busy
              ? "Analyzing..."
              : kind === "website"
                ? "Analyze My Website"
                : kind === "dns"
                  ? "Check DNS"
                  : kind === "domain"
                    ? "Look Up Domain"
                    : "Check PageSpeed"}
          </button>
        </div>
        {["website", "speed"].includes(kind) && (
          <fieldset className="segmented">
            <legend className="sr-only">Device</legend>
            {["mobile", "desktop"].map((s) => (
              <label key={s}>
                <input
                  type="radio"
                  name={"strategy-" + kind}
                  value={s}
                  checked={strategy === s}
                  onChange={() => setStrategy(s)}
                />
                <span>{s === "mobile" ? "Mobile" : "Desktop"}</span>
              </label>
            ))}
          </fieldset>
        )}
        <p className="privacy-note">
          No signup required. Public addresses only. Submitted URLs/domains are
          sent to BizAIPro and the applicable data providers for this check.
        </p>
      </form>
      {error && (
        <div className="tool-error" role="alert">
          {error}
        </div>
      )}
      {!busy && !result && !compact && (
        <p className="empty-state">
          Enter an address above to generate a report.
        </p>
      )}
      {result && (
        <div className="analysis-result" aria-live="polite">
          {kind === "website" ? (
            <>
              <div className="score-summary">
                <div className="score-number">
                  {result.score}
                  <small>/100</small>
                </div>
                <div>
                  <h2>{result.scoreLabel}</h2>
                  <p>{result.url}</p>
                  <small>{result.method}</small>
                </div>
              </div>
              <p>{result.note}</p>
              <div className="metrics-grid">
                {result.speed?.available ? (
                  Object.entries(result.speed.scores).map(([name, score]) => (
                    <div key={name}>
                      <span>{name.replaceAll("-", " ")}</span>
                      <strong>{score ?? "Unavailable"}</strong>
                      <small>Lighthouse lab score</small>
                    </div>
                  ))
                ) : (
                  <div className="provider-notice">
                    <strong>Performance data unavailable</strong>
                    <span>{result.speed?.reason}</span>
                  </div>
                )}
                <div>
                  <span>DNS lookup</span>
                  <strong>{result.dns ? "Available" : "Unavailable"}</strong>
                  <small>
                    {result.dns?.source || "No DNS score calculated"}
                  </small>
                </div>
              </div>
              <h3>Recommended fixes</h3>
              <div className="issue-list">
                {result.checks
                  .filter((c) => !c.passed)
                  .map((c) => (
                    <article key={c.id}>
                      <span
                        className={
                          "priority priority-" + c.priority.toLowerCase()
                        }
                      >
                        {c.priority}
                      </span>
                      <h4>{c.label}</h4>
                      <p>{c.why}</p>
                      <p>
                        <b>Next step:</b> {c.fix}
                      </p>
                    </article>
                  ))}
              </div>
              {result.checks.every((c) => c.passed) && (
                <p>All configuration checks in this report passed.</p>
              )}
              <details className="passed-checks">
                <summary>
                  {result.checks.filter((c) => c.passed).length} passed checks
                </summary>
                <ul>
                  {result.checks
                    .filter((c) => c.passed)
                    .map((c) => (
                      <li key={c.id}>
                        <Check size={16} />
                        {c.label}
                        {c.detail && <small>{c.detail}</small>}
                      </li>
                    ))}
                </ul>
              </details>
              <small>
                Checked {new Date(result.checkedAt).toLocaleString()}{" "}
                {result.cached ? "(cached report)" : ""}
              </small>
            </>
          ) : kind === "dns" ? (
            <>
              <h2>DNS results for {result.domain}</h2>
              <div className="metrics-grid">
                {Object.entries(result.summary).map(([k, v]) => (
                  <div key={k}>
                    <span>{k}</span>
                    <strong>{v === null ? "Unavailable" : v ? "Found" : "Not found"}</strong>
                  </div>
                ))}
              </div>
              {result.records.map((group) => (
                <details key={group.type} className="dns-record">
                  <summary>
                    {group.type} ·{" "}
                    {!group.available
                      ? "Unavailable"
                      : group.records.length + " records"}
                  </summary>
                  {group.records.map((r, i) => (
                    <div key={i}>
                      <code>{r.value}</code>
                      <small>TTL: {r.ttl}s</small>
                    </div>
                  ))}
                  {!group.records.length && <p>No records returned.</p>}
                </details>
              ))}
              <p>{result.note}</p>
              <a href="https://developers.google.com/speed/public-dns/">
                Source: {result.source}
              </a>
            </>
          ) : kind === "domain" ? (
            <>
              <h2>Registration: {result.domain}</h2>
              <dl className="domain-results">
                {[
                  ["Registration date", result.registration],
                  ["Age in years", result.ageYears],
                  ["Updated", result.updated],
                  ["Expiration", result.expiration],
                  ["Registrar", result.registrar],
                  ["Nameservers", result.nameservers.join(", ")],
                  ["Status", result.status.join(", ")],
                ].map(([k, v]) => (
                  <div key={k}>
                    <dt>{k}</dt>
                    <dd>{v === null || v === undefined || v === "" ? "Not publicly available" : v}</dd>
                  </div>
                ))}
              </dl>
              <p>{result.note}</p>
              <a href={result.source} target="_blank" rel="noopener noreferrer">
                View RDAP source
              </a>
            </>
          ) : (
            <>
              <h2>PageSpeed results</h2>
              {result.available ? (
                <>
                  <div className="metrics-grid">
                    {Object.entries(result.scores).map(([k, v]) => (
                      <div key={k}>
                        <span>{k}</span>
                        <strong>{v ?? "Unavailable"}</strong>
                      </div>
                    ))}
                  </div>
                  {result.metrics.map((m) => (
                    <p key={m.name}>
                      <b>{m.name}:</b> {m.value}
                    </p>
                  ))}
                  <p>{result.note}</p>
                  <a href="https://pagespeed.web.dev/">
                    Source: {result.source}
                  </a>
                </>
              ) : (
                <p>{result.reason}</p>
              )}
            </>
          )}
          <ResultActions
            text={JSON.stringify(result, null, 2)}
            name="bizaipro-report.json"
            type="application/json"
            share={kind === "website"}
          />
          <button
            type="button"
            className="button light small"
            onClick={() => window.print()}
          >
            <Printer size={16} />
            Print report
          </button>
        </div>
      )}
    </section>
  );
}
export function SchemaTool() {
  const [type, setType] = useState("Organization"),
    [values, setValues] = useState({}),
    [error, setError] = useState("");
  let output = "";
  try {
    output = JSON.stringify(publicSchema(type, values), null, 2);
  } catch {}
  const fields = ["BreadcrumbList"].includes(type)
    ? ["name", "url", "parentName", "parentUrl"]
    : [
        "name",
        "url",
        "description",
        ...(["Article", "BlogPosting"].includes(type)
          ? ["author", "datePublished"]
          : []),
        ...(type === "Product" ? ["price", "currency"] : []),
        ...(type === "Event" ? ["startDate", "location"] : []),
      ];
  return (
    <section className="tool-workspace">
      <label>
        Schema type
        <select
          value={type}
          onChange={(e) => {
            setType(e.target.value);
            setValues({});
          }}
        >
          {[
            "Organization",
            "LocalBusiness",
            "WebSite",
            "Article",
            "BlogPosting",
            "Product",
            "Service",
            "Event",
            "Person",
            "BreadcrumbList",
            "VideoObject",
            "Course",
            "SoftwareApplication",
          ].map((t) => (
            <option key={t}>{t}</option>
          ))}
        </select>
      </label>
      <div className="tool-columns">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            try {
              publicSchema(type, values);
              setError("");
            } catch (err) {
              setError(err.message);
            }
          }}
        >
          {fields.map((f) => (
            <label key={f}>
              {f.replace(/([A-Z])/g, " $1")}
              <input
                type={
                  f.toLowerCase().includes("url")
                    ? "url"
                    : f.toLowerCase().includes("date")
                      ? "date"
                      : "text"
                }
                value={values[f] || ""}
                onChange={(e) => setValues({ ...values, [f]: e.target.value })}
                required={f === "name" || f === "url"}
              />
            </label>
          ))}
          <button className="button">Validate fields</button>
          {error && <p role="alert">{error}</p>}
        </form>
        <div className="tool-output">
          <pre>{output || "Enter your name and URL to build JSON-LD."}</pre>
          {output && (
            <ResultActions
              text={output}
              name="schema.json"
              type="application/ld+json"
            />
          )}
        </div>
      </div>
      <p>
        Only supplied facts are included. This is a syntax helper, not a
        guarantee of rich-result eligibility. Validate your completed markup
        with Schema.org and the relevant search engine.
      </p>
    </section>
  );
}
export function UtmTool() {
  const [v, setV] = useState({}),
    [normalize, setNormalize] = useState(true),
    [dashes, setDashes] = useState(true),
    [qr, setQr] = useState("");
  let result = "",
    error = "";
  try {
    if (v.url) result = utmLink(v, { normalize, dashes });
  } catch (e) {
    error = e.message;
  }
  useEffect(() => {
    let cancel = false;
    if (!result) {
      setQr("");
      return;
    }
    import("qrcode")
      .then((q) => q.default.toDataURL(result, { width: 192, margin: 2 }))
      .then((url) => {
        if (!cancel) setQr(url);
      })
      .catch(() => setQr(""));
    return () => {
      cancel = true;
    };
  }, [result]);
  return (
    <section className="tool-workspace">
      <div className="tool-columns">
        <div>
          {["url", "source", "medium", "campaign", "term", "content"].map(
            (f) => (
              <label key={f}>
                {f === "url" ? "Destination URL" : "utm_" + f}
                <input
                  type={f === "url" ? "url" : "text"}
                  value={v[f] || ""}
                  onChange={(e) => setV({ ...v, [f]: e.target.value })}
                  placeholder={
                    f === "url"
                      ? "https://example.com"
                      : f === "campaign"
                        ? "spring-launch"
                        : ""
                  }
                />
              </label>
            ),
          )}
          <label className="check-label">
            <input
              type="checkbox"
              checked={normalize}
              onChange={(e) => setNormalize(e.target.checked)}
            />
            Lowercase campaign values
          </label>
          <label className="check-label">
            <input
              type="checkbox"
              checked={dashes}
              onChange={(e) => setDashes(e.target.checked)}
            />
            Replace spaces with dashes
          </label>
        </div>
        <div className="tool-output">
          <h3>Campaign URL</h3>
          <pre role="status">
            {error || result || "Enter a destination URL to build your link."}
          </pre>
          {result && (
            <>
              <ResultActions text={result} />
              {qr && (
                <>
                  <img
                    src={qr}
                    width="192"
                    height="192"
                    alt="QR code for your campaign URL"
                  />
                  <a
                    className="button light small"
                    href={qr}
                    download="utm-qr.png"
                  >
                    <Download size={16} />
                    Download QR
                  </a>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
}
export function QrTool() {
  const [text, setText] = useState(""),
    [png, setPng] = useState(""),
    [svg, setSvg] = useState(""),
    [error, setError] = useState("");
  async function generate(e) {
    e.preventDefault();
    setError("");
    try {
      const q = (await import("qrcode")).default;
      setPng(await q.toDataURL(text, { width: 512, margin: 2 }));
      setSvg(await q.toString(text, { type: "svg", margin: 2 }));
    } catch {
      setError("This content is too long for a QR code. Try a shorter value.");
    }
  }
  return (
    <section className="tool-workspace">
      <form onSubmit={generate}>
        <label>
          URL or text
          <textarea
            maxLength={2000}
            rows={4}
            required
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </label>
        <button className="button">Generate QR</button>
      </form>
      {error && <p role="alert">{error}</p>}
      {png && (
        <div className="qr-result">
          <img src={png} alt="Generated QR code" width="256" height="256" />
          <div className="result-actions">
            <a className="button light" href={png} download="bizaipro-qr.png">
              <Download size={16} />
              PNG
            </a>
            <button
              className="button light"
              onClick={() => save(svg, "bizaipro-qr.svg", "image/svg+xml")}
            >
              <Download size={16} />
              SVG
            </button>
          </div>
        </div>
      )}
      <p>
        Generated locally. You can enter a URL, text, mailto link, tel link or a
        correctly formatted Wi-Fi payload.
      </p>
    </section>
  );
}
export function PasswordTool() {
  const [password, setPassword] = useState(""),
    [busy, setBusy] = useState(false),
    [result, setResult] = useState("");
  async function check(e) {
    e.preventDefault();
    setBusy(true);
    setResult("");
    try {
      const bytes = await crypto.subtle.digest(
        "SHA-1",
        new TextEncoder().encode(password),
      );
      const hash = [...new Uint8Array(bytes)]
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("")
        .toUpperCase();
      setPassword("");
      const response = await fetch(
        "https://api.pwnedpasswords.com/range/" + hash.slice(0, 5),
        {
          headers: { "Add-Padding": "true" },
          signal: AbortSignal.timeout(15000),
        },
      );
      if (!response.ok)
        throw new Error("Exposure information is unavailable right now.");
      const rows = (await response.text()).split("\n");
      const count = Number(
        rows
          .find((row) => row.split(":")[0] === hash.slice(5))
          ?.split(":")[1] || 0,
      );
      setResult(
        count
          ? `This password has appeared ${count.toLocaleString()} times in the Pwned Passwords dataset. Replace it with a unique password.`
          : "Not found in the known Pwned Passwords dataset. This does not prove a password is safe.",
      );
    } catch {
      setResult(
        "The exposure check could not be completed. Please try again later.",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="tool-workspace">
      <form onSubmit={check}>
        <label>
          Password
          <input
            type="password"
            autoComplete="off"
            maxLength={1000}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>
        <button className="button" disabled={busy}>
          {busy ? "Checking..." : "Check Exposure"}
        </button>
      </form>
      <p>
        Your password is hashed locally. Only the first five hash characters are
        sent to Pwned Passwords. Neither your raw password nor the full hash is
        sent or saved.
      </p>
      <p role="status">{result}</p>
      <a href="https://haveibeenpwned.com/Passwords">
        Source: Have I Been Pwned Pwned Passwords
      </a>
      <p>
        Use unique passwords, a reputable password manager and multi-factor
        authentication where available.
      </p>
    </section>
  );
}
export function SerpTool() {
  const [v, setV] = useState({ title: "", description: "", url: "" }),
    [mobile, setMobile] = useState(false);
  return (
    <section className="tool-workspace">
      <div className="tool-columns">
        <div>
          {["title", "description", "url"].map((key) => (
            <label key={key}>
              {key}
              <textarea
                rows={key === "description" ? 4 : 2}
                value={v[key]}
                onChange={(e) => setV({ ...v, [key]: e.target.value })}
              />
            </label>
          ))}
          <p>
            {v.title.length} title characters · {v.description.length}{" "}
            description characters
          </p>
          <p>
            {v.title.length > 60 ? "Long title: it may be truncated. " : ""}
            {v.description.length > 160
              ? "Long description: it may be truncated."
              : ""}
          </p>
          <label className="check-label">
            <input
              type="checkbox"
              checked={mobile}
              onChange={(e) => setMobile(e.target.checked)}
            />
            Mobile approximation
          </label>
        </div>
        <div>
          <div className={mobile ? "serp-preview mobile" : "serp-preview"}>
            <span>{v.url || "https://example.com"}</span>
            <h3>{v.title || "Your page title"}</h3>
            <p>{v.description || "Your page description appears here."}</p>
          </div>
          <p>
            Approximation only. Search engines may rewrite titles and
            descriptions; display width depends on device, font and query.
          </p>
        </div>
      </div>
    </section>
  );
}
export function SavingsTool() {
  const [v, setV] = useState(["", "", "", "", ""]),
    [result, setResult] = useState(null),
    [error, setError] = useState("");
  const labels = [
    "Employees involved",
    "Hourly labor cost",
    "Repetitive hours per employee per week",
    "Potential automation (%)",
    "Monthly tool cost",
  ];
  return (
    <section className="tool-workspace">
      <div className="tool-columns">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            try {
              setResult(savingsResult(v));
              setError("");
            } catch (err) {
              setError(err.message);
            }
          }}
        >
          {labels.map((label, i) => (
            <label key={label}>
              {label}
              <input
                type="number"
                min="0"
                max={i === 3 ? 100 : undefined}
                step="any"
                required
                value={v[i]}
                onChange={(e) =>
                  setV(v.map((x, n) => (n === i ? e.target.value : x)))
                }
              />
            </label>
          ))}
          <button className="button">Calculate savings</button>
        </form>
        <div className="tool-output">
          {error && <p role="alert">{error}</p>}
          {result ? (
            <>
              <dl className="domain-results">
                {Object.entries(result).map(([key, value]) => (
                  <div key={key}>
                    <dt>{key.replace(/([A-Z])/g, " $1")}</dt>
                    <dd>{value ?? "Not defined with zero cost"}</dd>
                  </div>
                ))}
              </dl>
              <ResultActions text={JSON.stringify(result, null, 2)} share />
            </>
          ) : (
            <p>Enter your assumptions to estimate monthly savings.</p>
          )}
        </div>
      </div>
      <p>
        This is an estimate based on user-supplied assumptions, using 52 weeks /
        12 months. Labor value represents freed capacity, not guaranteed cash
        savings.
      </p>
    </section>
  );
}
export function InvoiceTool() {
  const [form, setForm] = useState({
      business: "",
      businessAddress: "",
      client: "",
      clientAddress: "",
      number: "",
      issued: "",
      due: "",
      currency: "USD",
      tax: "0",
      discount: "0",
      notes: "",
    }),
    [items, setItems] = useState([
      { description: "", quantity: "1", price: "0" },
    ]),
    [error, setError] = useState("");
  let totals;
  try {
    totals = invoiceTotals(items, form.tax, form.discount);
  } catch {}
  const money = (n) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: /^[A-Z]{3}$/.test(form.currency) ? form.currency : "USD",
    }).format(n);
  return (
    <section className="invoice-tool">
      <div className="invoice-editor">
        <div className="form-grid">
          {[
            ["business", "Business name"],
            ["businessAddress", "Business address"],
            ["client", "Client name"],
            ["clientAddress", "Client address"],
            ["number", "Invoice number"],
            ["issued", "Issue date"],
            ["due", "Due date"],
            ["currency", "Currency (ISO code)"],
            ["tax", "Tax rate (%)"],
            ["discount", "Discount amount"],
          ].map(([key, label]) => (
            <label key={key}>
              {label}
              <input
                type={
                  ["issued", "due"].includes(key)
                    ? "date"
                    : ["tax", "discount"].includes(key)
                      ? "number"
                      : "text"
                }
                min="0"
                value={form[key]}
                onChange={(e) =>
                  setForm({
                    ...form,
                    [key]:
                      key === "currency"
                        ? e.target.value.toUpperCase()
                        : e.target.value,
                  })
                }
              />
            </label>
          ))}
        </div>
        <h3>Invoice items</h3>
        {items.map((item, index) => (
          <div className="invoice-row" key={index}>
            <label>
              Description
              <input
                value={item.description}
                onChange={(e) =>
                  setItems(
                    items.map((x, i) =>
                      i === index ? { ...x, description: e.target.value } : x,
                    ),
                  )
                }
              />
            </label>
            <label>
              Quantity
              <input
                type="number"
                min="0"
                step="any"
                value={item.quantity}
                onChange={(e) =>
                  setItems(
                    items.map((x, i) =>
                      i === index ? { ...x, quantity: e.target.value } : x,
                    ),
                  )
                }
              />
            </label>
            <label>
              Unit price
              <input
                type="number"
                min="0"
                step="any"
                value={item.price}
                onChange={(e) =>
                  setItems(
                    items.map((x, i) =>
                      i === index ? { ...x, price: e.target.value } : x,
                    ),
                  )
                }
              />
            </label>
            <button
              type="button"
              title="Remove item"
              aria-label="Remove item"
              disabled={items.length === 1}
              onClick={() => setItems(items.filter((_, i) => i !== index))}
            >
              <Trash2 size={18} />
            </button>
          </div>
        ))}
        <button
          type="button"
          className="button light"
          disabled={items.length >= 50}
          onClick={() =>
            setItems([...items, { description: "", quantity: "1", price: "0" }])
          }
        >
          <Plus size={16} />
          Add item
        </button>
        <label>
          Notes
          <textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </label>
      </div>
      <div className="invoice-preview">
        <h2>INVOICE</h2>
        <b>{form.business || "Your business"}</b>
        <p className="preserve-lines">{form.businessAddress}</p>
        <p>
          <b>Bill to:</b> {form.client || "Client name"}
          <br />
          {form.clientAddress}
        </p>
        <p>
          Invoice {form.number} · Issued {form.issued || "Not set"} · Due{" "}
          {form.due || "Not set"}
        </p>
        {items.map((item, i) => (
          <div className="invoice-preview-row" key={i}>
            <span>
              {item.description || "Item"}
              <small>
                {item.quantity} × {money(Number(item.price) || 0)}
              </small>
            </span>
            <strong>
              {money((Number(item.quantity) || 0) * (Number(item.price) || 0))}
            </strong>
          </div>
        ))}
        {totals && (
          <dl className="domain-results">
            {Object.entries(totals).map(([key, value]) => (
              <div key={key}>
                <dt>{key}</dt>
                <dd>{money(value)}</dd>
              </div>
            ))}
          </dl>
        )}
        <p className="preserve-lines">{form.notes}</p>
      </div>
      <div className="result-actions">
        <button
          className="button"
          type="button"
          onClick={() => {
            try {
              invoiceTotals(items, form.tax, form.discount);
              setError("");
              window.print();
            } catch (err) {
              setError(err.message);
            }
          }}
        >
          <Printer size={16} />
          Print / Save PDF
        </button>
        {totals && (
          <ResultActions
            text={JSON.stringify({ invoice: form, items, totals }, null, 2)}
            name="invoice.json"
            type="application/json"
          />
        )}
      </div>
      {error && <p role="alert">{error}</p>}
      <p>
        Invoice data stays in this page's memory. Nothing is uploaded or
        permanently saved. Print / Save PDF uses your browser's PDF printer.
      </p>
    </section>
  );
}
